import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import api from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create context
const AdminContext = createContext(null);

// Constants for badge options
export const BEER_BADGE_OPTIONS = [
  { value: "new", label: "New", color: "bg-emerald-500 text-black" },
  { value: "limited", label: "Limited", color: "bg-red-500 text-white" },
  { value: "local", label: "Local", color: "bg-blue-500 text-white" },
  { value: "seasonal", label: "Seasonal", color: "bg-purple-500 text-white" },
  { value: "featured", label: "Featured", color: "bg-amber-500 text-black" },
  { value: "best-selling", label: "Best Selling", color: "bg-yellow-500 text-black" },
  { value: "customer-favorite", label: "Customer Favorite", color: "bg-pink-500 text-white" },
  { value: "gotta-try", label: "Gotta Try", color: "bg-cyan-500 text-black" },
  { value: "ridiculous-flavor", label: "Ridiculous Flavor", color: "bg-orange-500 text-black" },
];

export const FOOD_BADGE_OPTIONS = [
  { value: "popular", label: "Popular", color: "bg-amber-500 text-black" },
  { value: "spicy", label: "Spicy", color: "bg-red-500 text-white" },
  { value: "vegetarian", label: "Vegetarian", color: "bg-emerald-500 text-black" },
  { value: "gluten-free", label: "GF", color: "bg-blue-500 text-white" },
  { value: "new", label: "New", color: "bg-purple-500 text-white" },
];

export const BLEND_BADGE_OPTIONS = [
  { value: "staff-pick", label: "Staff Pick", color: "bg-amber-500 text-black" },
  { value: "bold", label: "Bold", color: "bg-red-500 text-white" },
  { value: "smooth", label: "Smooth", color: "bg-blue-500 text-white" },
  { value: "adventurous", label: "Adventurous", color: "bg-purple-500 text-white" },
  { value: "crowd-favorite", label: "Crowd Favorite", color: "bg-emerald-500 text-black" },
];

export const EVENT_BADGE_OPTIONS = [
  { value: "featured", label: "Featured", color: "bg-amber-500 text-black" },
  { value: "new", label: "New", color: "bg-emerald-500 text-black" },
  { value: "live-music", label: "Live Music", color: "bg-purple-500 text-white" },
  { value: "trivia", label: "Trivia", color: "bg-blue-500 text-white" },
  { value: "special", label: "Special", color: "bg-red-500 text-white" },
  { value: "weekly", label: "Weekly", color: "bg-cyan-500 text-black" },
];

export const BEER_STYLES = [
  "IPA", "Double IPA", "Hazy IPA", "Pale Ale", "Lager", "Pilsner", 
  "Stout", "Porter", "Wheat", "Hefeweizen", "Sour", "Saison", 
  "Belgian", "Amber", "Brown Ale", "Kolsch", "Cream Ale", "Cider", "Other"
];

export const FOOD_CATEGORIES = [
  "Appetizers", "Wings", "Burgers", "Sandwiches", "Pizza", "Tacos",
  "Salads", "Sides", "Desserts", "Specials"
];

// Provider component
export function AdminProvider({ children }) {
  const { getAuthHeader } = useAuth();
  
  // Global settings state
  const [settings, setSettings] = useState({
    slideshow_enabled: true,
    slideshow_interval: 45,
    auto_scroll_delay: 3,
    scroll_pause_duration: 3,
    scroll_speed: 0.4,
    taps_auto_scroll: true,
    blends_auto_scroll: true,
    food_auto_scroll: true,
    fame_auto_scroll: true,
    events_auto_scroll: true,
    taps_margin_left: 20,
    taps_margin_right: 20,
    taps_gap: 16
  });
  
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Update settings
  const updateSettings = useCallback(async (updates) => {
    try {
      const response = await api.put('/settings', updates);
      setSettings(response.data);
      return response.data;
    } catch (error) {
      toast.error("Failed to update settings");
      throw error;
    }
  }, [getAuthHeader]);

  // Get badge style helper
  const getBadgeStyle = useCallback((badge, badgeType = 'all') => {
    let badgeOptions = [];
    if (badgeType === 'beer') badgeOptions = settings.beer_badges || [];
    else if (badgeType === 'food') badgeOptions = settings.food_badges || [];
    else if (badgeType === 'blend') badgeOptions = settings.blend_badges || [];
    else if (badgeType === 'event') badgeOptions = settings.event_badges || [];
    else {
      badgeOptions = [
        ...(settings.beer_badges || []),
        ...(settings.food_badges || []),
        ...(settings.blend_badges || []),
        ...(settings.event_badges || [])
      ];
    }
    
    const found = badgeOptions.find(b => b.value === badge);
    if (found) return found.color;
    
    // Fallback to default badge styles
    const defaultStyles = {
      new: "bg-emerald-500 text-black",
      limited: "bg-red-500 text-white",
      local: "bg-blue-500 text-white",
      seasonal: "bg-purple-500 text-white",
      featured: "bg-amber-500 text-black",
      popular: "bg-amber-500 text-black",
      spicy: "bg-red-500 text-white",
      vegetarian: "bg-emerald-500 text-black",
      "gluten-free": "bg-blue-500 text-white",
      "staff-pick": "bg-amber-500 text-black",
      bold: "bg-red-500 text-white",
      smooth: "bg-blue-500 text-white",
      adventurous: "bg-purple-500 text-white",
      "crowd-favorite": "bg-emerald-500 text-black",
      "live-music": "bg-purple-500 text-white",
      trivia: "bg-blue-500 text-white",
      special: "bg-red-500 text-white",
      weekly: "bg-cyan-500 text-black",
      "best-selling": "bg-yellow-500 text-black",
      "customer-favorite": "bg-pink-500 text-white",
      "gotta-try": "bg-cyan-500 text-black",
      "ridiculous-flavor": "bg-orange-500 text-black",
      AI: "bg-gradient-to-r from-violet-500 to-purple-600 text-white"
    };
    
    return defaultStyles[badge] || "bg-zinc-600 text-white";
  }, [settings]);

  // Initial fetch
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const value = {
    settings,
    settingsLoading,
    fetchSettings,
    updateSettings,
    getBadgeStyle,
    API
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

// Hook to use the context
export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
}

export default AdminContext;
